import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';


const Header = ({ userRole = 'user', userName = 'User', userAvatar = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const navigationItems = [
    { label: 'Projects', path: '/project-management', icon: 'FolderOpen' },
    { label: 'Create', path: '/credit-creation', icon: 'Plus' },
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Render', path: '/video-rendering', icon: 'Play' },
  ];

  const adminItems = [
    { label: 'Admin', path: '/admin-dashboard', icon: 'Shield' },
    { label: 'Users', path: '/user-management', icon: 'Users' },
  ];

  const userMenuItems = [
    { label: 'Account Settings', path: '/account-settings', icon: 'Settings' },
    { label: 'Help', path: '/help', icon: 'HelpCircle' },
    { label: 'Logout', action: 'logout', icon: 'LogOut' },
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleUserMenuAction = (item) => {
    if (item?.action === 'logout') {
      // Handle logout logic
      navigate('/user-login');
    } else {
      navigate(item?.path);
    }
    setIsUserMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const Logo = () => (
    <div className="flex items-center space-x-2">
      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
        <Icon name="Film" size={20} color="white" />
      </div>
      <span className="text-xl font-bold text-primary font-sans">RealRoll</span>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-navigation shadow-elevation-1">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center">
          <button
            onClick={() => navigate('/user-dashboard')}
            className="transition-micro hover:opacity-80"
          >
            <Logo />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-micro ${
                isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </button>
          ))}

          {/* Admin Navigation - Conditional */}
          {(userRole === 'admin' || userRole === 'superadmin') && (
            <>
              <div className="w-px h-6 bg-border mx-2" />
              {adminItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-micro ${
                    isActive(item?.path)
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.label}</span>
                </button>
              ))}
            </>
          )}
        </nav>

        {/* User Menu & Mobile Toggle */}
        <div className="flex items-center space-x-2">
          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-micro"
            >
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                {userAvatar ? (
                  <img src={userAvatar} alt={userName} className="w-8 h-8 rounded-full" />
                ) : (
                  <Icon name="User" size={16} color="white" />
                )}
              </div>
              <span className="hidden md:block text-sm font-medium text-foreground">{userName}</span>
              <Icon 
                name="ChevronDown" 
                size={16} 
                className={`transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {/* User Dropdown */}
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevation-2 z-dropdown">
                <div className="p-2">
                  {userMenuItems?.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleUserMenuAction(item)}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-popover-foreground hover:bg-muted rounded-md transition-micro"
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-micro"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="p-4 space-y-2">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-micro ${
                  isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </button>
            ))}

            {/* Admin Navigation - Mobile */}
            {(userRole === 'admin' || userRole === 'superadmin') && (
              <>
                <div className="h-px bg-border my-3" />
                <div className="text-xs font-medium text-muted-foreground px-4 py-2">Administration</div>
                {adminItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-micro ${
                      isActive(item?.path)
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </button>
                ))}
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;